import React, { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { DialogModal, Drawer, Text } from "@pushpress/shared-components";
import { defaultToastOptions, extractError } from "../../../utils/misc";
import { AppContext } from "../../../context/app";
import {
  END_TIME_DEFAULT,
  START_TIME_DEFAULT,
  convertToFormValues,
  convertToSaveVariables,
  findAllSameDayAndSort,
  getNextWorkingHours,
  validateWorkingHours,
  workingHoursDefault,
} from "./WorkingHoursDrawer.utils";
import { WorkingHourForm } from "./WorkingHoursDrawer.types";
import WorkingHourController from "./WorkingHourController";
import { DrawerPanel } from "../../../components/common/DrawerPanel";
import {
  useGetProfileForAvailabilityQuery,
  useDeleteAllWorkingHoursMutation,
  useGetWorkingHoursQuery,
  useSaveWorkingHoursMutation,
} from "../Availability-generated.hooks";

interface WorkingHoursDrawerProps {
  memberId: string;
  visible: boolean;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
}

function WorkingHoursDrawer(props: WorkingHoursDrawerProps) {
  const { visible } = props;
  return (
    <Drawer
      visible={visible}
      anchor="right"
      width="400px"
      disableClickAway
      disableEscapeKeyDown
    >
      <WorkingHoursDrawerContent {...props} />
    </Drawer>
  );
}

function WorkingHoursDrawerContent(props: WorkingHoursDrawerProps) {
  const { memberId, onClose, onSuccess } = props;

  const app = useContext(AppContext);

  const [confirmCloseVisible, setConfirmCloseVisible] = useState(false);

  const { data: { getProfile: profile } = {} } =
    useGetProfileForAvailabilityQuery({
      variables: {
        input: {
          clientUuid: app.client.client_uuid,
          userUuid: memberId,
          clientUserUuid: undefined,
          linkedAccounts: undefined,
        },
      },
      onError: (error) => {
        toast.error(extractError(error), defaultToastOptions);
      },
    });

  const { data: { getWorkingHours: workingHours } = {} } =
    useGetWorkingHoursQuery({
      variables: {
        input: {
          userUuid: memberId,
        },
      },
      fetchPolicy: "no-cache",
    });

  const [saveWorkingHours, { loading: saveLoading }] =
    useSaveWorkingHoursMutation();

  const [deleteWorkingHours, { loading: deleteLoading }] =
    useDeleteAllWorkingHoursMutation();

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { isDirty },
  } = useForm<WorkingHourForm>({
    mode: "onTouched",
    defaultValues: {
      workingHours: workingHoursDefault,
    },
  });

  const { fields, remove, insert, update } = useFieldArray({
    control,
    name: "workingHours",
  });

  const formValues = useWatch({
    control,
  });

  const save = async (values: WorkingHourForm) => {
    try {
      if (isDirty) {
        const allEmpty = values.workingHours.every((item) => !item.enabled);
        if (allEmpty) {
          await deleteWorkingHours({
            variables: {
              input: {
                userUuid: memberId,
              },
            },
          });
        } else {
          const variables = convertToSaveVariables(memberId, values);
          await saveWorkingHours({
            variables,
          });
        }
      }
      toast.success("Working hours saved successfully.", defaultToastOptions);
      onSuccess();
    } catch (error) {
      toast.error(extractError(error), defaultToastOptions);
    }
  };

  const handleSave = () => {
    handleSubmit(save)();
  };

  const handleClose = () => {
    if (isDirty) {
      setConfirmCloseVisible(true);
    } else {
      onClose();
    }
  };

  const handleAdd = (index: number) => {
    const workingHour = formValues.workingHours![index];
    if (!workingHour.enabled) {
      update(index, {
        ...workingHour,
        dayOfTheWeek: workingHour.dayOfTheWeek!,
        enabled: true,
      });
    } else {
      const periodsOfTheSameDay = findAllSameDayAndSort(
        formValues.workingHours!,
        workingHour.dayOfTheWeek!,
      );
      const lastWorkingHour =
        periodsOfTheSameDay[periodsOfTheSameDay.length - 1];
      const nextWorkingHour = getNextWorkingHours(lastWorkingHour);
      insert(index + periodsOfTheSameDay.length, nextWorkingHour);
    }
  };

  const handleRemove = (index: number) => {
    const workingHour = formValues.workingHours![index];
    const totalWithTheSameWeekDay = formValues.workingHours!.filter(
      (item) => item.dayOfTheWeek === workingHour.dayOfTheWeek,
    ).length;
    if (totalWithTheSameWeekDay > 1) {
      remove(index);
    } else {
      update(index, {
        ...workingHour,
        dayOfTheWeek: workingHour.dayOfTheWeek!,
        startTime: START_TIME_DEFAULT,
        endTime: END_TIME_DEFAULT,
        enabled: false,
      });
    }
  };

  const validateForm = (index: number) => {
    return validateWorkingHours(formValues.workingHours!, index);
  };

  // set the working hours from the query data.
  useEffect(() => {
    reset(convertToFormValues(workingHours));
  }, [reset, workingHours]);

  // force validation messages on filled working hours.
  useEffect(() => {
    formValues.workingHours?.forEach((item, index) => {
      if (item.startTime && item.endTime) {
        trigger(`workingHours.${index}`);
      }
    });
  }, [trigger, formValues.workingHours]);

  const loading = !profile || !workingHours;
  const saving = saveLoading || deleteLoading;

  return (
    <>
      <DrawerPanel
        title="Working hours"
        loading={loading}
        saving={saving}
        onClose={handleClose}
        onSave={handleSave}
      >
        <Text variant="body-lg" className="mb-4">
          Set the days and hours that {profile?.firstName} is generally
          available for appointment bookings.
        </Text>
        <form onSubmit={handleSubmit(save)}>
          {fields.map((item, index) => {
            const isTheSameWeekDayThanPrevious =
              formValues.workingHours?.[index - 1]?.dayOfTheWeek ===
              item.dayOfTheWeek;
            return (
              <WorkingHourController
                control={control}
                name={`workingHours.${index}`}
                key={item.id}
                hideWeekDay={isTheSameWeekDayThanPrevious}
                hideAddButton={isTheSameWeekDayThanPrevious}
                hideDeleteButton={!item.enabled}
                validate={() => validateForm(index)}
                onDelete={() => handleRemove(index)}
                onAdd={() => handleAdd(index)}
              />
            );
          })}
          <button type="submit" hidden />
        </form>
      </DrawerPanel>
      <DialogModal
        title="Discard unsaved changes?"
        visible={confirmCloseVisible}
        primaryBtnTitle="Discard"
        secondaryBtnTitle="Go back"
        primaryHandler={onClose}
        secondaryHandler={() => setConfirmCloseVisible(false)}
        closeModal={() => setConfirmCloseVisible(false)}
      />
    </>
  );
}

export default WorkingHoursDrawer;
